/**
 * VariableFactory
 *
 * Creates properly-typed FunnelVariable instances with appropriate defaults.
 * Validates that type-specific fields are provided (e.g., options for enum/enum[]).
 *
 * @module v2-variable-utils
 */

import type { FunnelVariable, VariableType, VariableOption } from '@segmently/schema-models/v2';
import type { CreateVariableParams } from '../types/variable-utils.types';

export class VariableFactory {
  /**
   * Create a properly-typed FunnelVariable with defaults.
   * Type-specific fields are only set when matching the variable type.
   *
   * @param params - Variable creation parameters
   * @returns Complete FunnelVariable
   */
  create(params: CreateVariableParams): FunnelVariable {
    const id = params.id || this.generateId(params.name);
    const now = new Date();

    const base: FunnelVariable = {
      id,
      name: params.name,
      type: params.type,
      required: params.required ?? false,
      excludeFromAnalytics: params.excludeFromAnalytics,
      scope: (params.scope ?? 'screen') as FunnelVariable['scope'],
      description: params.description,
      tags: params.tags,
      taskIds: params.taskIds,
      order: params.order,
      createdAt: now,
      updatedAt: now,
    };

    // Pass through defaultValue if provided
    if (params.defaultValue !== undefined) {
      (base as unknown as Record<string, unknown>).defaultValue = params.defaultValue;
    }

    // Add type-specific fields
    switch (params.type) {
      case 'number':
        if (params.min !== undefined) base.min = params.min;
        if (params.max !== undefined) base.max = params.max;
        if (params.integer !== undefined) base.integer = params.integer;
        break;

      case 'date':
        if (params.minDate) base.minDate = params.minDate;
        if (params.maxDate) base.maxDate = params.maxDate;
        break;

      case 'enum':
        base.options = params.options ?? [];
        break;

      case 'enum[]':
        base.options = params.options ?? [];
        if (params.minSelections !== undefined) base.minSelections = params.minSelections;
        if (params.maxSelections !== undefined) base.maxSelections = params.maxSelections;
        break;
    }

    return base;
  }

  /**
   * Generate a unique variable ID from a name.
   * Converts to lowercase snake_case slug.
   *
   * @param name - Human-readable name (e.g., "User Goal")
   * @param existingIds - Optional set of existing IDs to avoid collisions
   * @returns Unique slug ID (e.g., "user_goal")
   */
  generateId(name: string, existingIds?: string[]): string {
    let slug = name
      .toLowerCase()
      .trim()
      .replace(/[^a-z0-9\s_-]/g, '')
      .replace(/[\s-]+/g, '_')
      .replace(/_+/g, '_')
      .replace(/^_|_$/g, '');

    if (!slug) {
      slug = 'variable';
    }

    if (!existingIds || !existingIds.includes(slug)) {
      return slug;
    }

    // Append counter for uniqueness
    let counter = 2;
    while (existingIds.includes(`${slug}_${counter}`)) {
      counter++;
    }
    return `${slug}_${counter}`;
  }

  /**
   * Create a default variable for a given type with placeholder values.
   */
  createDefault(type: VariableType): FunnelVariable {
    const defaults: Record<VariableType, Partial<CreateVariableParams>> = {
      'string': { name: 'New String Variable' },
      'number': { name: 'New Number Variable', min: 0, max: 100 },
      'boolean': { name: 'New Boolean Variable' },
      'date': { name: 'New Date Variable' },
      'enum': {
        name: 'New Enum Variable',
        options: [
          { id: 'option_1', label: 'Option 1' },
          { id: 'option_2', label: 'Option 2' },
        ],
      },
      'enum[]': {
        name: 'New Multi-Select Variable',
        options: [
          { id: 'option_1', label: 'Option 1' },
          { id: 'option_2', label: 'Option 2' },
        ],
        minSelections: 1,
      },
    };

    return this.create({
      ...defaults[type],
      name: defaults[type]?.name ?? 'New Variable',
      type,
    });
  }
}

/** Singleton instance */
export const variableFactory = new VariableFactory();
